import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import VoiceLogo from "@/components/VoiceLogo";
import AnimatedFeatureCard from "@/components/AnimatedFeatureCard";
import HeroAppMockup from "@/components/HeroAppMockup";
import axios from "axios";
import { toast } from "sonner";
import { 
  Mic, 
  CheckCircle, 
  Sparkles, 
  Languages, 
  Volume2, 
  Zap,
  ArrowRight,
  Play,
  Download,
  Apple,
  Monitor,
  Star,
  Shield,
  Square,
  Loader2,
  Copy
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

// Live Demo Component - Looks exactly like the actual app
function LiveDemoSection() {
  const [isRecording, setIsRecording] = useState(false);
  const [transcription, setTranscription] = useState("");
  const [recordingTime, setRecordingTime] = useState(0);
  const [demoCount, setDemoCount] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [activeFunction, setActiveFunction] = useState(null);
  const [selectedTone, setSelectedTone] = useState("professional");
  const [selectedLanguage, setSelectedLanguage] = useState("Spanish");
  const [showToneDropdown, setShowToneDropdown] = useState(false);
  const [showLangDropdown, setShowLangDropdown] = useState(false);
  const [detectedLanguage, setDetectedLanguage] = useState("English");
  const [showCopied, setShowCopied] = useState(false);
  
  const MAX_DEMO_TRANSCRIPTIONS = 3;
  const MAX_RECORDING_SECONDS = 15;
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);

  const tones = [
    { id: "original", name: "Original", icon: "↩️" },
    { id: "conversational", name: "Conversational", icon: "💬" },
    { id: "personal", name: "Personal", icon: "👤" },
    { id: "professional", name: "Professional", icon: "💼" },
    { id: "direct", name: "Direct", icon: "➡️" },
    { id: "empathetic", name: "Empathetic", icon: "💝" },
  ];

  const languages = [
    { code: "es", name: "Spanish", flag: "🇪🇸" },
    { code: "fr", name: "French", flag: "🇫🇷" },
    { code: "de", name: "German", flag: "🇩🇪" },
    { code: "it", name: "Italian", flag: "🇮🇹" },
    { code: "pt", name: "Portuguese", flag: "🇵🇹" },
    { code: "zh", name: "Chinese", flag: "🇨🇳" },
    { code: "ja", name: "Japanese", flag: "🇯🇵" },
  ];

  useEffect(() => {
    if (isRecording && recordingTime >= MAX_RECORDING_SECONDS) {
      stopRecording();
    }
  }, [recordingTime, isRecording]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const startRecording = async () => {
    if (demoCount >= MAX_DEMO_TRANSCRIPTIONS) {
      toast.error("Demo limit reached! Sign up for unlimited access.");
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        await transcribeAudio(audioBlob);
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      timerRef.current = setInterval(() => setRecordingTime(t => t + 1), 1000);
    } catch (err) {
      toast.error("Please allow microphone access to try the demo.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
      }
    }
  };

  const transcribeAudio = async (audioBlob) => {
    setIsProcessing(true);
    setActiveFunction("transcribe");
    
    try {
      const formData = new FormData();
      formData.append("audio", audioBlob, "demo_recording.webm");
      formData.append("language", "en");

      const response = await axios.post(`${API}/demo/transcribe`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 30000
      });

      if (response.data.text) {
        let text = response.data.text;
        setDemoCount(prev => prev + 1);
        
        // Auto-fix errors
        try {
          const fixResponse = await axios.post(`${API}/demo/fix-errors`, { text });
          if (fixResponse.data.corrected && fixResponse.data.corrected !== text) {
            text = fixResponse.data.corrected;
            setTranscription(text);
            await copyToClipboardSafe(text);
            toast.success("Transcribed, auto-fixed & copied!", { duration: 3000 });
          } else {
            setTranscription(text);
            await copyToClipboardSafe(text);
            toast.success("Transcribed & copied to clipboard!", { duration: 3000 });
          }
        } catch (fixError) {
          setTranscription(text);
          await copyToClipboardSafe(text);
          toast.success("Transcribed & copied to clipboard!", { duration: 3000 });
        }
      }
    } catch (error) {
      // Fallback for demo
      const demoText = "Hey team, let's meet tomorrow at 3 PM to discuss the quarterly report.";
      setTranscription(demoText);
      setDemoCount(prev => prev + 1);
      await copyToClipboardSafe(demoText);
      toast.info("Demo mode - Copied sample text");
    } finally {
      setIsProcessing(false);
      setActiveFunction(null);
    }
  };

  // Safe clipboard copy that doesn't require permission prompt
  const copyToClipboardSafe = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      try {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      } catch (fallbackErr) {
        console.error('Clipboard copy failed:', fallbackErr);
      }
    }
  };

  const handleFunction = async (func) => {
    if (!transcription) {
      toast.error("Record or enter some text first!");
      return;
    }

    setIsProcessing(true);
    setActiveFunction(func);

    try {
      let response;
      switch (func) {
        case "fix":
          response = await axios.post(`${API}/demo/fix-errors`, { text: transcription }, { timeout: 15000 });
          if (response.data.corrected) setTranscription(response.data.corrected);
          toast.success("Errors fixed!");
          break;
        case "tone":
          response = await axios.post(`${API}/demo/change-tone`, { text: transcription, tone: selectedTone }, { timeout: 15000 });
          if (response.data.transformed) setTranscription(response.data.transformed);
          toast.success(`Tone changed to ${selectedTone}!`);
          break;
        case "translate":
          response = await axios.post(`${API}/demo/translate`, { text: transcription, language: selectedLanguage }, { timeout: 15000 });
          if (response.data.translated) setTranscription(response.data.translated);
          toast.success(`Translated to ${selectedLanguage}!`);
          break;
        case "speak":
          const utterance = new SpeechSynthesisUtterance(transcription);
          speechSynthesis.speak(utterance);
          toast.success("Reading aloud...");
          break;
        default:
          break;
      }
    } catch (error) {
      toast.error("Demo feature - Sign up for full access!");
    } finally {
      setIsProcessing(false);
      setActiveFunction(null);
    }
  };

  const copyToClipboard = () => {
    if (!transcription) return;
    navigator.clipboard.writeText(transcription);
    setShowCopied(true);
    setTimeout(() => setShowCopied(false), 1500);
  };

  const clearText = () => {
    setTranscription("");
  };

  // Voice visualizer bars
  const VoiceVisualizerBars = () => (
    <div className="flex items-center gap-[2px] h-6">
      {[...Array(20)].map((_, i) => (
        <div
          key={i}
          className="w-[3px] rounded-full transition-all duration-150"
          style={{
            height: isRecording ? `${6 + Math.random() * 18}px` : '4px',
            backgroundColor: isRecording ? '#22d3ee' : '#374151'
          }}
        />
      ))}
    </div>
  );

  return (
    <section className="py-12 px-4" style={{ backgroundColor: '#1a2332' }}>
      <div className="max-w-xl mx-auto">
        <div className="text-center mb-6">
          <h2 className="text-2xl sm:text-3xl font-bold mb-2">Try It Now — No Sign Up Required</h2>
          <p className="text-sm" style={{ color: '#a0aec0' }}>
            {MAX_DEMO_TRANSCRIPTIONS - demoCount} recording{MAX_DEMO_TRANSCRIPTIONS - demoCount !== 1 ? 's' : ''} remaining • 15 sec max • Unlimited functions
          </p>
        </div>

        {/* App-Like Demo Container - Matches DictationPage exactly */}
        <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: '#0f1419', border: '1px solid #2d3748' }}>
          
          {/* Header - Same as real app */}
          <div className="px-4 py-3 flex items-center justify-between" style={{ backgroundColor: '#1a2332', borderBottom: '1px solid #2d3748' }}>
            <div className="flex items-center gap-2">
              {isRecording && (
                <>
                  <div className="w-2.5 h-2.5 rounded-full animate-pulse" style={{ backgroundColor: '#ef4444' }} />
                  <span className="text-sm font-mono" style={{ color: '#ef4444' }}>{formatTime(recordingTime)}</span>
                </>
              )}
            </div>
            <button
              onClick={() => handleFunction("speak")}
              disabled={isProcessing || !transcription}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all hover:opacity-90"
              style={{ backgroundColor: '#10b981', color: '#fff', opacity: !transcription ? 0.5 : 1 }}
            >
              <Volume2 className="w-3.5 h-3.5" />
              Read Aloud
            </button>
          </div>

          {/* Main Content Area */}
          <div className="p-4 flex flex-col items-center gap-3">
            
            {/* Logo + Title - Same as real app */}
            <div className="flex flex-col items-center gap-1">
              <VoiceLogo size={40} />
              <h1 className="text-lg font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Voice Capture</h1>
            </div>

            {/* Visualizer + Language Detection - Same as real app */}
            <div className="flex items-center justify-center gap-2 flex-wrap">
              <VoiceVisualizerBars />
              <div className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs" style={{ backgroundColor: '#1a2332', border: '1px solid #2d3748' }}>
                <span style={{ color: '#a0aec0' }}>Detected:</span>
                <span className="font-bold" style={{ color: '#10b981' }}>{detectedLanguage}</span>
              </div>
            </div>

            {/* Text Area - Same as real app */}
            <div className="w-full rounded-xl overflow-hidden" style={{ backgroundColor: '#1a2332', border: '1px solid #2d3748' }}>
              <textarea
                value={transcription}
                onChange={(e) => setTranscription(e.target.value)}
                placeholder="Press the microphone button. When done speaking, press the microphone button again and text will appear here."
                className="w-full h-28 border-none focus:ring-0 focus:outline-none resize-none text-sm leading-relaxed p-3 overflow-y-auto"
                style={{ backgroundColor: 'transparent', color: '#f5f5f5', fontFamily: 'IBM Plex Sans, sans-serif' }}
              />
            </div>

            {/* ROW 1: Copy, History, Shortcuts, Download, Clear - Same as real app */}
            <div className="flex flex-wrap items-center justify-center gap-1.5">
              <button onClick={copyToClipboard} disabled={!transcription} className="flex items-center gap-1 px-2.5 h-7 rounded-full text-[11px] font-medium transition-all hover:opacity-90" style={{ backgroundColor: showCopied ? '#22c55e' : '#4b5563', color: '#fff', border: '1px solid #6b7280', opacity: !transcription ? 0.5 : 1 }}>
                <Copy className="w-3 h-3" /> {showCopied ? 'Copied!' : 'Copy'}
              </button>
              <button className="flex items-center gap-1 px-2.5 h-7 rounded-full text-[11px] font-medium" style={{ backgroundColor: '#4b5563', color: '#fff', border: '1px solid #6b7280' }}>
                📜 History
              </button>
              <button className="flex items-center gap-1 px-2.5 h-7 rounded-full text-[11px] font-medium" style={{ backgroundColor: '#3b82f6', color: '#fff' }}>
                ⚡ Shortcuts
              </button>
              <button className="flex items-center gap-1 px-2.5 h-7 rounded-full text-[11px] font-medium" style={{ backgroundColor: '#4b5563', color: '#fff', border: '1px solid #6b7280' }}>
                <Download className="w-3 h-3" /> Download
              </button>
              <button onClick={clearText} className="px-2.5 h-7 rounded-full text-[11px] font-medium" style={{ backgroundColor: '#4b5563', color: '#fff', border: '1px solid #6b7280' }}>
                Clear
              </button>
            </div>

            {/* ROW 2: Tone + Translate - Same as real app */}
            <div className="flex flex-wrap items-center justify-center gap-1.5">
              {/* Tone Dropdown */}
              <div className="relative">
                <button 
                  onClick={() => { setShowToneDropdown(!showToneDropdown); setShowLangDropdown(false); }}
                  className="flex items-center gap-1 px-2.5 h-7 rounded-full text-[11px] font-medium"
                  style={{ backgroundColor: '#8b5cf6', color: '#fff' }}
                >
                  <Sparkles className="w-3 h-3" />
                  Tone ▼
                </button>
                {showToneDropdown && (
                  <div className="absolute top-full left-0 mt-1 py-1 rounded-lg shadow-xl z-20" style={{ backgroundColor: '#1a2332', border: '1px solid #2d3748', minWidth: '150px' }}>
                    {tones.map(tone => (
                      <button
                        key={tone.id}
                        onClick={() => { setSelectedTone(tone.id); setShowToneDropdown(false); handleFunction("tone"); }}
                        className="w-full px-3 py-1.5 text-left text-xs hover:bg-purple-500/20 flex items-center gap-2"
                        style={{ color: '#f5f5f5' }}
                      >
                        <span>{tone.icon}</span> {tone.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Translate Dropdown + GO */}
              <div className="relative">
                <button 
                  onClick={() => { setShowLangDropdown(!showLangDropdown); setShowToneDropdown(false); }}
                  className="flex items-center gap-1 px-2.5 h-7 rounded-full text-[11px] font-medium"
                  style={{ backgroundColor: '#6366f1', color: '#fff' }}
                >
                  <Languages className="w-3 h-3" />
                  Translate ▼
                </button>
                {showLangDropdown && (
                  <div className="absolute top-full left-0 mt-1 py-1 rounded-lg shadow-xl z-20 max-h-48 overflow-y-auto" style={{ backgroundColor: '#1a2332', border: '1px solid #2d3748', minWidth: '140px' }}>
                    {languages.map(lang => (
                      <button
                        key={lang.code}
                        onClick={() => { setSelectedLanguage(lang.name); setShowLangDropdown(false); }}
                        className="w-full px-3 py-1.5 text-left text-xs hover:bg-purple-500/20 flex items-center gap-2"
                        style={{ color: '#f5f5f5' }}
                      >
                        <span>{lang.flag}</span> {lang.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <span className="flex items-center gap-1 px-2 h-7 rounded-full text-[11px]" style={{ backgroundColor: '#1a2332', border: '1px solid #2d3748', color: '#a0aec0' }}>
                🇪🇸 {selectedLanguage}
              </span>
              <button 
                onClick={() => handleFunction("translate")}
                disabled={isProcessing || !transcription}
                className="px-3 h-7 rounded-full text-[11px] font-bold transition-all hover:opacity-90"
                style={{ backgroundColor: '#10b981', color: '#fff', opacity: !transcription ? 0.5 : 1 }}
              >
                {activeFunction === "translate" ? <Loader2 className="w-3 h-3 animate-spin" /> : "GO"}
              </button>
            </div>

            {/* Record Button - Same as real app */}
            <div className="flex flex-col items-center gap-2 mt-2">
              <button
                onClick={isRecording ? stopRecording : startRecording}
                disabled={isProcessing && activeFunction === "transcribe"}
                className={`w-20 h-20 rounded-full flex items-center justify-center transition-all hover:scale-105 ${isRecording ? 'animate-pulse' : ''}`}
                style={{
                  backgroundColor: isRecording ? '#ef4444' : demoCount >= MAX_DEMO_TRANSCRIPTIONS ? '#374151' : '#3b82f6',
                  boxShadow: isRecording ? '0 0 30px rgba(239, 68, 68, 0.7)' : '0 0 20px rgba(59, 130, 246, 0.5)',
                  opacity: demoCount >= MAX_DEMO_TRANSCRIPTIONS ? 0.5 : 1
                }}
              >
                {isRecording ? (
                  <Square className="w-7 h-7 text-white" fill="white" />
                ) : isProcessing && activeFunction === "transcribe" ? (
                  <Loader2 className="w-7 h-7 text-white animate-spin" />
                ) : (
                  <Mic className="w-8 h-8 text-white" />
                )}
              </button>
              <p className="text-sm" style={{ color: '#a0aec0' }}>
                {isRecording 
                  ? `Recording... Click or press again to transcribe`
                  : isProcessing && activeFunction === "transcribe"
                    ? "Processing..."
                    : demoCount >= MAX_DEMO_TRANSCRIPTIONS
                      ? "Demo limit reached — Sign up for unlimited!"
                      : "Click or press to record"}
              </p>
              
              {/* Keyboard shortcuts hint */}
              <div className="flex items-center gap-4 text-[10px] mt-2" style={{ color: '#718096' }}>
                <span className="flex items-center gap-1">
                  <kbd className="px-2 py-0.5 rounded text-[9px] font-bold" style={{ backgroundColor: '#3b82f6', color: '#fff' }}>Ctrl+Win</kbd>
                  <span>Hold to record</span>
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="px-2 py-0.5 rounded text-[9px] font-bold" style={{ backgroundColor: '#22c55e', color: '#fff' }}>Ctrl+V</kbd>
                  <span>Paste</span>
                </span>
              </div>
              <p className="text-[10px] mt-1" style={{ color: '#22c55e' }}>
                ✨ Auto-fixes errors & copies to clipboard
              </p>
            </div>
          </div>

          {/* Privacy Footer */}
          <div className="px-4 py-2 text-center" style={{ borderTop: '1px solid #2d3748' }}>
            <p className="text-[10px]" style={{ color: '#718096' }}>
              <Shield className="w-3 h-3 inline mr-1" />
              Private — We don't store your demo recordings
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0f1419', color: '#f5f5f5' }}>
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 px-4 py-3" style={{ backgroundColor: 'rgba(15, 20, 25, 0.95)', backdropFilter: 'blur(10px)', borderBottom: '1px solid #2d3748' }}>
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <VoiceLogo size={32} />
            <span className="text-xl font-bold">Voice Capture</span>
          </div>
          <div className="flex items-center gap-4">
            <Button onClick={() => navigate('/auth')} className="rounded-full px-6" style={{ backgroundColor: '#6366f1' }}>
              Sign In
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section - Premium Redesign */}
      <section className="pt-20 pb-8 px-4 overflow-hidden relative">
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 rounded-full opacity-10 blur-3xl" style={{ backgroundColor: '#6366f1' }} />
          <div className="absolute bottom-20 right-10 w-96 h-96 rounded-full opacity-10 blur-3xl" style={{ backgroundColor: '#22d3ee' }} />
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full opacity-5 blur-3xl" style={{ backgroundColor: '#8b5cf6' }} />
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          {/* 65% text / 35% simulation layout */}
          <div className="grid lg:grid-cols-[65%_35%] gap-6 items-center">
            {/* Left Column - Content (65%) */}
            <div className="text-center lg:text-left">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6" style={{ backgroundColor: 'rgba(99, 102, 241, 0.15)', border: '1px solid rgba(99, 102, 241, 0.3)' }}>
                <div className="flex -space-x-1">
                  <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                </div>
                <span className="text-sm font-medium" style={{ color: '#a0aec0' }}>Loved by 10,000+ users</span>
              </div>

              {/* Main Headline */}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                <span className="text-white">Stop typing.</span>
                <br />
                <span style={{ 
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #22d3ee 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}>
                  Start speaking.
                </span>
              </h1>

              {/* Subheadline */}
              <p className="text-xl mb-6 leading-relaxed max-w-xl mx-auto lg:mx-0" style={{ color: '#a0aec0' }}>
                <span className="text-white font-semibold">Talk at any speed</span> with near 100% accuracy. 
                Plus automatic grammar fixes, tone changes, and translation — 
                <span className="text-purple-400 font-semibold"> all in one app</span>.
              </p>

              {/* Value Props */}
              <div className="flex flex-wrap gap-4 justify-center lg:justify-start mb-8">
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ backgroundColor: '#22c55e20' }}>
                    <CheckCircle className="w-3 h-3" style={{ color: '#22c55e' }} />
                  </div>
                  <span className="text-sm" style={{ color: '#a0aec0' }}>98% accuracy</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ backgroundColor: '#22c55e20' }}>
                    <Shield className="w-3 h-3" style={{ color: '#22c55e' }} />
                  </div>
                  <span className="text-sm" style={{ color: '#a0aec0' }}>100% private</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ backgroundColor: '#22c55e20' }}>
                    <Sparkles className="w-3 h-3" style={{ color: '#22c55e' }} />
                  </div>
                  <span className="text-sm" style={{ color: '#a0aec0' }}>AI-powered</span>
                </div>
              </div>

              {/* CTA Button - Centered with Pulsate Animation */}
              <div className="flex flex-col items-center gap-3 mb-6">
                <Button 
                  onClick={() => navigate('/signup')}
                  className="rounded-full px-10 py-7 text-xl font-semibold flex items-center justify-center gap-2 group animate-pulse-glow"
                  style={{ 
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    boxShadow: '0 10px 40px rgba(99, 102, 241, 0.4)'
                  }}
                >
                  Try Free
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>

              {/* Trust Text */}
              <p className="text-sm" style={{ color: '#718096' }}>
                No credit card required • Cancel anytime
              </p>
            </div>

            {/* Right Column - App Mockup (35%) */}
            <div className="flex justify-center lg:justify-center">
              <HeroAppMockup />
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Live Demo Section */}
      <LiveDemoSection />

      {/* Features Section - Animated Cards */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">Everything You Need</h2>
            <p className="text-base" style={{ color: '#a0aec0' }}>Powerful features that make voice capture effortless</p>
          </div>

          {/* 9 Animated Feature Cards - All Features */}
          <div className="flex flex-wrap justify-center gap-4">
            <div className="w-full sm:w-[calc(50%-8px)] lg:w-[calc(33.333%-11px)] xl:w-[calc(25%-12px)]">
              <AnimatedFeatureCard type="transcription" />
            </div>
            <div className="w-full sm:w-[calc(50%-8px)] lg:w-[calc(33.333%-11px)] xl:w-[calc(25%-12px)]">
              <AnimatedFeatureCard type="errors" />
            </div>
            <div className="w-full sm:w-[calc(50%-8px)] lg:w-[calc(33.333%-11px)] xl:w-[calc(25%-12px)]">
              <AnimatedFeatureCard type="tone" />
            </div>
            <div className="w-full sm:w-[calc(50%-8px)] lg:w-[calc(33.333%-11px)] xl:w-[calc(25%-12px)]">
              <AnimatedFeatureCard type="translate" />
            </div>
            <div className="w-full sm:w-[calc(50%-8px)] lg:w-[calc(33.333%-11px)] xl:w-[calc(25%-12px)]">
              <AnimatedFeatureCard type="readaloud" />
            </div>
            <div className="w-full sm:w-[calc(50%-8px)] lg:w-[calc(33.333%-11px)] xl:w-[calc(25%-12px)]">
              <AnimatedFeatureCard type="emojishortcuts" />
            </div>
            <div className="w-full sm:w-[calc(50%-8px)] lg:w-[calc(33.333%-11px)] xl:w-[calc(25%-12px)]">
              <AnimatedFeatureCard type="createtypes" />
            </div>
            <div className="w-full sm:w-[calc(50%-8px)] lg:w-[calc(33.333%-11px)] xl:w-[calc(25%-12px)]">
              <AnimatedFeatureCard type="shortcuts" />
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-16 px-4" style={{ backgroundColor: '#1a2332' }}>
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">Why Voice Capture?</h2>
            <p style={{ color: '#a0aec0' }}>Compare and see the difference</p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr style={{ borderBottom: '1px solid #2d3748' }}>
                  <th className="py-4 px-4 text-left font-semibold">Feature</th>
                  <th className="py-4 px-4 text-center font-semibold" style={{ color: '#a78bfa' }}>Voice Capture</th>
                  <th className="py-4 px-4 text-center font-semibold" style={{ color: '#718096' }}>Others</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ borderBottom: '1px solid #2d3748' }}>
                  <td className="py-4 px-4">Voice Transcription</td>
                  <td className="py-4 px-4 text-center"><CheckCircle className="w-5 h-5 mx-auto" style={{ color: '#22c55e' }} /></td>
                  <td className="py-4 px-4 text-center"><CheckCircle className="w-5 h-5 mx-auto" style={{ color: '#22c55e' }} /></td>
                </tr>
                <tr style={{ borderBottom: '1px solid #2d3748' }}>
                  <td className="py-4 px-4">Fix Grammar & Errors</td>
                  <td className="py-4 px-4 text-center"><CheckCircle className="w-5 h-5 mx-auto" style={{ color: '#22c55e' }} /></td>
                  <td className="py-4 px-4 text-center" style={{ color: '#ef4444' }}>✕</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #2d3748' }}>
                  <td className="py-4 px-4">11 Tone Options</td>
                  <td className="py-4 px-4 text-center"><CheckCircle className="w-5 h-5 mx-auto" style={{ color: '#22c55e' }} /></td>
                  <td className="py-4 px-4 text-center" style={{ color: '#ef4444' }}>✕</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #2d3748' }}>
                  <td className="py-4 px-4">40+ Language Translation</td>
                  <td className="py-4 px-4 text-center"><CheckCircle className="w-5 h-5 mx-auto" style={{ color: '#22c55e' }} /></td>
                  <td className="py-4 px-4 text-center" style={{ color: '#ef4444' }}>✕</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #2d3748' }}>
                  <td className="py-4 px-4">Read Aloud (AI Voice)</td>
                  <td className="py-4 px-4 text-center"><CheckCircle className="w-5 h-5 mx-auto" style={{ color: '#22c55e' }} /></td>
                  <td className="py-4 px-4 text-center" style={{ color: '#ef4444' }}>✕</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #2d3748' }}>
                  <td className="py-4 px-4">Custom Shortcuts</td>
                  <td className="py-4 px-4 text-center"><CheckCircle className="w-5 h-5 mx-auto" style={{ color: '#22c55e' }} /></td>
                  <td className="py-4 px-4 text-center" style={{ color: '#718096' }}>Limited</td>
                </tr>
                <tr>
                  <td className="py-4 px-4 font-semibold">Price</td>
                  <td className="py-4 px-4 text-center font-bold" style={{ color: '#22c55e' }}>From $2.99/mo</td>
                  <td className="py-4 px-4 text-center" style={{ color: '#ef4444' }}>$40+/mo</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 px-4" style={{ backgroundColor: '#1a2332' }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-4">Simple, Transparent Pricing</h2>
          <p className="mb-12" style={{ color: '#a0aec0' }}>Start free. Upgrade when you need more.</p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Free Trial */}
            <div className="p-6 rounded-2xl" style={{ backgroundColor: '#0f1419', border: '1px solid #2d3748' }}>
              <div className="text-3xl font-bold mb-2">Free</div>
              <div className="text-sm mb-4" style={{ color: '#718096' }}>Try before you buy</div>
              <ul className="text-left space-y-2 mb-6">
                <li className="flex items-center gap-2 text-sm">
                  <CheckCircle className="h-4 w-4" style={{ color: '#22c55e' }} /> 10 minutes transcription
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <CheckCircle className="h-4 w-4" style={{ color: '#22c55e' }} /> All features included
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <CheckCircle className="h-4 w-4" style={{ color: '#22c55e' }} /> No credit card required
                </li>
              </ul>
              <Button onClick={() => navigate('/signup')} className="w-full rounded-full" style={{ backgroundColor: '#374151' }}>
                Start Free Trial
              </Button>
            </div>

            {/* Standard */}
            <div className="p-6 rounded-2xl" style={{ backgroundColor: '#0f1419', border: '1px solid #2d3748' }}>
              <div className="text-3xl font-bold mb-2">$2.99<span className="text-lg font-normal">/mo</span></div>
              <div className="text-sm mb-4" style={{ color: '#718096' }}>Perfect for everyday use</div>
              <ul className="text-left space-y-2 mb-6">
                <li className="flex items-center gap-2 text-sm">
                  <CheckCircle className="h-4 w-4" style={{ color: '#22c55e' }} /> 100 min transcription
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <CheckCircle className="h-4 w-4" style={{ color: '#22c55e' }} /> Edit up to 25 documents
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <CheckCircle className="h-4 w-4" style={{ color: '#22c55e' }} /> All features included
                </li>
              </ul>
              <Button onClick={() => navigate('/signup')} className="w-full rounded-full" style={{ backgroundColor: '#6366f1' }}>
                Get Standard
              </Button>
            </div>

            {/* Pro */}
            <div className="p-6 rounded-2xl relative" style={{ backgroundColor: '#0f1419', border: '2px solid #6366f1' }}>
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 px-3 py-1 text-xs font-bold rounded-full" style={{ backgroundColor: '#6366f1' }}>
                MOST POPULAR
              </div>
              <div className="text-3xl font-bold mb-2 mt-2">$4.99<span className="text-lg font-normal">/mo</span></div>
              <div className="text-sm mb-4" style={{ color: '#718096' }}>Best for heavy users</div>
              <ul className="text-left space-y-2 mb-6">
                <li className="flex items-center gap-2 text-sm">
                  <CheckCircle className="h-4 w-4" style={{ color: '#22c55e' }} /> Unlimited transcription
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <CheckCircle className="h-4 w-4" style={{ color: '#22c55e' }} /> Unlimited editing
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <CheckCircle className="h-4 w-4" style={{ color: '#22c55e' }} /> Priority support
                </li>
              </ul>
              <Button onClick={() => navigate('/signup')} className="w-full rounded-full" style={{ backgroundColor: '#6366f1' }}>
                Get Pro
              </Button>
            </div>
          </div>

          <p className="mt-8 text-sm" style={{ color: '#718096' }}>
            Cancel anytime. No long-term contracts.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4" style={{ borderTop: '1px solid #2d3748' }}>
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <VoiceLogo size={24} />
            <span className="font-semibold">Voice Capture</span>
          </div>
          <div className="flex items-center gap-6 text-sm" style={{ color: '#718096' }}>
            <button onClick={() => navigate('/terms')} className="hover:text-white transition-colors">Terms & Conditions</button>
            <button onClick={() => navigate('/auth')} className="hover:text-white transition-colors">Sign In</button>
            <a href="mailto:support@voicecapture.net" className="hover:text-white transition-colors">Support</a>
          </div>
          <div className="text-sm" style={{ color: '#718096' }}>
            © 2025 Voice Capture. All rights reserved.
          </div>
        </div>
      </footer>

      {/* CSS for animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out;
        }
        @keyframes pulse-glow {
          0%, 100% {
            box-shadow: 0 10px 40px rgba(99, 102, 241, 0.4);
            transform: scale(1);
          }
          50% {
            box-shadow: 0 15px 50px rgba(99, 102, 241, 0.6), 0 0 30px rgba(139, 92, 246, 0.4);
            transform: scale(1.02);
          }
        }
        .animate-pulse-glow {
          animation: pulse-glow 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
